import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../common/PageModal'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import GoogleAddressInput from '../common/GoogleAddressInput'
import { addUser } from '../../actions/users'
import countries from '../../utils/countries.json'
import { capitalizeFirstLetter, formatPhone } from '../../utils/functions'
import languages, { getLanguageFromLocale } from '../../utils/languages'
import { usersWritePermission } from '../../utils/permissionValidation'
import { getUserAccountTypes } from '../../utils/requests/usersAPI'

const AddUser = (props) => {
  const { t } = useTranslation()
  const { accountType, onCreateUser, closeModal, showSpinner, setShowSpinner } = props
  const [account, setAccount] = useState(null)
  const [role, setRole] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [language, setLanguage] = useState(null)
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [groupPracticeName, setGroupPracticeName] = useState('')
  const [country, setCountry] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateProvince, setStateProvice] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [accountsWithRoles, setAccountsWithRoles] = useState([])
  const [haveError, setHaveError] = useState({})
  const phonePattern = new RegExp(/^\d*$/)

  useEffect(() => {
    setShowSpinner(true)

    getUserAccountTypes()
      .then((res) => {
        const mappedAccountsWithRoles = res.map((account) => ({
          id: account.id,
          name: account.name,
          children: account.userGroups.map((role) => ({
            id: role.id,
            name: role.name,
          })),
        }))
        setAccountsWithRoles(mappedAccountsWithRoles)

        if (accountType) {
          setAccount(mappedAccountsWithRoles.find((a) => a.name === accountType)?.id)
        }
      })
      .finally(() => setShowSpinner(false))
  }, [])

  const userDetailsFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button is-edit-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button is-edit-button"
          disabled={showSpinner}
          onClick={() => handleUpdateUser()}
          requiredPermission={usersWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const selectAccount = (accountId) => {
    if (account !== accountId) {
      setRole(null)
    }

    setAccount(accountId)
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!account) {
      errors = { ...errors, account: true }
      isValid = false
    }

    if (!role) {
      errors = { ...errors, role: true }
      isValid = false
    }

    if (firstName.trim().length === 0) {
      errors = { ...errors, firstName: true }
      isValid = false
    }
    if (lastName.trim().length === 0) {
      errors = { ...errors, lastName: true }
      isValid = false
    }

    if (phone.trim().length === 0 || !phonePattern.test(formatPhone(phone).trim().replaceAll('-', ''))) {
      errors = { ...errors, phone: true }
      isValid = false
    }

    if (!language) {
      errors = { ...errors, language: true }
      isValid = false
    }

    if (email.trim().length === 0) {
      errors = { ...errors, email: true }
      isValid = false
    }

    if (companyName.trim().length === 0) {
      errors = { ...errors, companyName: true }
      isValid = false
    }

    if (!country) {
      errors = { ...errors, country: true }
      isValid = false
    }

    if (address.trim().length === 0) {
      errors = { ...errors, address: true }
      isValid = false
    }

    if (city.trim().length === 0) {
      errors = { ...errors, city: true }
      isValid = false
    }

    if (stateProvince.trim().length === 0) {
      errors = { ...errors, stateProvince: true }
      isValid = false
    }

    if (zipCode.trim().length === 0) {
      errors = { ...errors, zipCode: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleUpdateUser = () => {
    const selectedAccountName = accountsWithRoles.find((a) => a.id === account)?.name
    if (email.trim().length && selectedAccountName === 'Coltene' && !(email.trim().endsWith('@coltene.com') || email.trim().endsWith('@scican.com'))) {
      setHaveError({ ...haveError, email: true })

      toast.error(t('error.only_scican_email_supported'))
      return
    }

    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const bodyParams = {
      roleId: role,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      company: companyName.trim(),
      ...(groupPracticeName.trim().length > 0 && { groupPracticeName: groupPracticeName.trim() }),
      email: email.trim(),
      addressLine1: address.trim(),
      city: city.trim(),
      state: stateProvince.trim(),
      zip: zipCode.trim(),
      locale: language,
      phone: formatPhone(phone).replaceAll('-', ''),
      country: country,
      newsletter: true,
    }

    props.actions.addUser(bodyParams).then((res) => {
      if (onCreateUser) {
        onCreateUser(bodyParams)
      }

      if (res?.action?.payload?.error) {
        toast.warning(t(`error.${res.action.payload.error.code}`))
      }

      closeModal()
    })
  }

  return (
    <PageModal toggle onToggle={() => closeModal()} title={t('dealer-portal.add_user')} footerContent={userDetailsFooterContent()}>
      <div className="user-details-modal-content">
        <div className="user-details-item-wrapper">
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('account')}*`}</p>
            </div>
            {!accountType && (
              <Dropdown
                className="dropdown-input"
                placeholder={t('users-management.select-account')}
                options={accountsWithRoles?.map((accountOption) => ({ key: accountOption.id, value: accountOption.name }))}
                initialSelectedKey={account}
                onChange={(account) => selectAccount(account.key)}
                hasError={haveError['account']}
              />
            )}
            {accountType && <div className="value">{accountType}</div>}
          </div>
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('role')}*`}</p>
            </div>
            <Dropdown
              className="dropdown-input"
              placeholder={capitalizeFirstLetter(t('select_role'))}
              options={accountsWithRoles?.find((a) => a.id === account)?.children?.map((roleOption) => ({ key: roleOption.id, value: roleOption.name }))}
              initialSelectedKey={role}
              onChange={(role) => setRole(role.key)}
              hasError={haveError['role']}
            />
          </div>
        </div>
        <div className="user-details-item-wrapper">
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('first_name')}*`}</p>
            </div>
            <input
              type="text"
              className={'input-item' + (haveError['firstName'] ? ' has-error' : '')}
              value={firstName || ''}
              onChange={(e) => setFirstName(e.target.value)}
            ></input>
          </div>
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('last_name')}*`}</p>
            </div>
            <input
              type="text"
              className={'input-item' + (haveError['lastName'] ? ' has-error' : '')}
              value={lastName || ''}
              onChange={(e) => setLastName(e.target.value)}
            ></input>
          </div>
        </div>
        <div className="user-details-item-wrapper">
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('phone')}*`}</p>
            </div>
            <input
              type="text"
              className={'input-item' + (haveError['phone'] ? ' has-error' : '')}
              value={formatPhone(phone) ?? ''}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
            />
          </div>
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('language')}*`}</p>
            </div>
            <Dropdown
              className="dropdown-input"
              placeholder={t('users-management.select-language')}
              options={Object.keys(languages).map((languageOption) => ({
                key: languageOption,
                value: t([`${getLanguageFromLocale(languageOption)}`, '']),
              }))}
              initialSelectedKey={language}
              onChange={(language) => setLanguage(language.key)}
              hasError={haveError['language']}
            />
          </div>
        </div>
        <div className="user-details-item-wrapper single-item">
          <div className="user-details-item full-width-item">
            <div className="title">
              <p>{`${t('email')}*`}</p>
            </div>
            <input
              type="text"
              className={'input-item' + (haveError['email'] ? ' has-error' : '')}
              value={email || ''}
              onChange={(e) => setEmail(e.target.value)}
            ></input>
          </div>
        </div>
        <div className={'user-details-item-wrapper' + (accountsWithRoles.find((a) => a.id === account)?.name === 'End User' ? '' : ' single-item')}>
          <div className="user-details-item">
            <div className="title">
              <p>{`${t('organization_context.company_name')}*`}</p>
            </div>
            <input
              type="text"
              className={'input-item' + (haveError['companyName'] ? ' has-error' : '')}
              value={companyName || ''}
              onChange={(e) => setCompanyName(e.target.value)}
            ></input>
          </div>
          {accountsWithRoles.find((a) => a.id === account)?.name === 'End User' && (
            <div className="user-details-item">
              <div className="title">
                <p>
                  {t('group_practice_name')}
                  <span className="small-label">{` (${t('if_applicable')})`}</span>
                </p>
              </div>
              <input type="text" className="input-item" value={groupPracticeName ?? ''} onChange={(e) => setGroupPracticeName(e.target.value)}></input>
            </div>
          )}
        </div>
        <div className={'user-details-item-wrapper single-item'}>
          <div className="user-details-item full-width-item with-margin-bottom">
            <div className="title">
              <p>{`${t('country')}*`}</p>
            </div>
            <Dropdown
              className="dropdown-input"
              placeholder={t('users-management.select-country')}
              options={countries?.map((countryOption) => ({ key: countryOption['code'], value: countryOption['name'] }))}
              initialSelectedKey={country}
              onChange={(country) => setCountry(country.key)}
              withFiltering
              hasError={haveError['country']}
            />
          </div>
        </div>
        <GoogleAddressInput
          country={country}
          address={address}
          handleSetAddress={(e) => setAddress(e)}
          city={city}
          handleSetCity={(e) => setCity(e)}
          stateProvince={stateProvince}
          handleSetStateProvince={(e) => setStateProvice(e)}
          zipCode={zipCode}
          handleSetZipCode={(e) => setZipCode(e)}
          errors={haveError}
          withoutPlaceholders
        />
      </div>
    </PageModal>
  )
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        addUser,
      },
      dispatch,
    ),
  }
}

export default connect(null, dispatchToProps)(AddUser)

